import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/partials/button/button';
import toast from 'components/partials/toast/toast';
import DiscardConfirmModal from './discard-confirm-modal';

import { useCreateInsight } from 'hooks/use-create-insight';

import {
  reset as resetInsight,
  setStep as setStepAction,
} from 'state/insight/insight.slice';
import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { unmountModal } from 'helpers/modal-render';

import {
  INSIGHT_MODAL_CLOSE,
  INSIGHT_MODAL_NAVIGATE_REQUEST,
} from 'components/partials/insight-modal/shared/constants';
import type { InsightModalMetadata } from 'components/partials/insight-modal/shared/constants';

import type { RootState } from 'state/store';

import Icons from 'assets/icons';

const dataURItoBlob = async (
  dataURI: string,
  filename = 'screenshot.png',
): Promise<File> => {
  try {
    const res = await fetch(dataURI);
    const blob = await res.blob();

    return new File([blob], filename, { type: blob.type });
  } catch (error) {
    console.error('Failed to convert data URL to blob:', error);
    throw error;
  }
};

const InsightDefaultFooter = () => {
  const dispatch = useDispatch();
  const step = useSelector((s: RootState) => s?.insight?.step || 1);
  const insightState = useSelector((s: RootState) => s?.insight);
  const currentUser = useCurrentUser();

  const [discardNavigateTo, setDiscardNavigateTo] = useState<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const { createInsightWithData, loading } = useCreateInsight();

  useEffect(() => {
    const handleClose = () => {
      setDiscardNavigateTo(null);
      setShowDiscardConfirm(true);
    };

    window.addEventListener(INSIGHT_MODAL_CLOSE, handleClose);

    return () => {
      window.removeEventListener(INSIGHT_MODAL_CLOSE, handleClose);
    };
  }, []);

  useEffect(() => {
    const handleNavigateRequest = (e: Event) => {
      const url = (e as CustomEvent<{ url?: string }>).detail?.url;

      if (url) {
        setDiscardNavigateTo(url);
        setShowDiscardConfirm(true);
      }
    };

    window.addEventListener(
      INSIGHT_MODAL_NAVIGATE_REQUEST,
      handleNavigateRequest
    );

    return () => {
      window.removeEventListener(
        INSIGHT_MODAL_NAVIGATE_REQUEST,
        handleNavigateRequest
      );
    };
  }, []);

  const isLastStep = (): boolean => {
    const metadata = insightState?.metadata as InsightModalMetadata | undefined;
    const isReport = metadata?.isReport;

    return (step === 3 && !isReport) || (step === 4 && !!isReport);
  };

  const isNextStepDisabled = (): boolean => {
    const metadata = (insightState?.metadata || {}) as InsightModalMetadata;
    const isReport = metadata?.isReport;

    if ((step === 2 && !isReport) || (step === 3 && isReport)) {
      const hasTitle =
        typeof metadata?.title === 'string' && metadata.title.trim().length > 0;
      const hasDescription =
        typeof metadata?.description === 'string' &&
        metadata.description.trim().length > 0;

      return !hasTitle || !hasDescription;
    }

    return false;
  };

  const handleNextStep = () => {
    const metadata = insightState?.metadata as InsightModalMetadata | undefined;
    const isReport = metadata?.isReport;

    if (step === 3 && !isReport) {
      dispatch(setStepAction(4));
    } else if (step === 4 && isReport) {
      dispatch(setStepAction(5));
    } else {
      dispatch(setStepAction(step + 1));
    }
  };

  const handleFinish = async () => {
    try {
      const userId = currentUser?.data?.oktaUserId;
      const metadata = (insightState?.metadata || {}) as InsightModalMetadata;
      const assignedUsers = insightState?.assignees || [];
      const imageDataURL = insightState?.imageUrl;
      const autoAssignToUsers = insightState?.autoAssignToUsers || false;

      const actualClientId =
        metadata?.clientId || currentUser?.data?.client?.id;

      if (!userId || !actualClientId || !imageDataURL) {
      toast.error({ title: 'Cannot create insight', message: 'Missing required data. Please try again.' });

        return;
      }

      const file = await dataURItoBlob(imageDataURL, 'insight-screenshot.png');

      const insightData = {
        name: metadata.title ?? '',
        description: metadata.description,
        metadata: {
          source:
            metadata.source === 'report' ? metadata.name : metadata.summary,
          relatedGoals: metadata.relatedGoals,
          insightType: metadata.source === 'report' ? 'report' : 'monitoring',
          ...(metadata.source === 'report' && {
            reportActivePage: metadata.reportActivePage,
            reportFilters: metadata.reportFilters,
            sourceId: metadata.reportId,
          }),
          ...(metadata.source === 'monitoring' && {
            sourceId: metadata.criticalElementId,
          }),
        },
        autoAssignToUsers,
      };

      await createInsightWithData({
        userId,
        clientId: Number(actualClientId),
        insightData,
        file,
        assignedUsers,
      });

      toast.success({ title: 'Insight created successfully!', message: 'Your insight has been saved and is ready for use.' });

      dispatch(resetInsight());
      unmountModal();
    } catch (err) {
      console.error('Failed to create insight:', err);

      toast.error({ title: 'Failed to create insight', message: (err as Error)?.message || 'An unexpected error occurred. Please try again.' });
    }
  };

  const handleDiscardConfirmOpen = () => {
    setDiscardNavigateTo(null);
    setShowDiscardConfirm(true);
  };

  const handleDiscardConfirmCancel = () => {
    setDiscardNavigateTo(null);
    setShowDiscardConfirm(false);
  };

  const handleDiscardConfirmClose = () => {
    setDiscardNavigateTo(null);
    dispatch(resetInsight());
    unmountModal();
  };

  return (
    <div className="flex justify-between items-center w-full">
      <div>
        {[2, 3, 4].includes(step) && (
          <Button
            variant="reserve-invert"
            size="sm"
            onClick={() => dispatch(setStepAction(step - 1))}
            className="group"
            disabled={loading}
            iconBefore={
              <Icons.Arrow customClass="rotate-180 transition-transform duration-300 group-hover:-translate-x-1" />
            }
          >
            Back
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="reserve-outlined"
          size="sm"
          onClick={handleDiscardConfirmOpen}
          disabled={loading}
          data-testid="cancel-insight-button"
        >
          Cancel
        </Button>
        <Button
          variant="brand-filled"
          size="sm"
          onClick={isLastStep() ? handleFinish : handleNextStep}
          disabled={loading || isNextStepDisabled()}
          loading={loading}
          data-testid="next-insight-button"
        >
          {loading && isLastStep()
            ? 'Creating...'
            : isLastStep()
            ? 'Add insight'
            : 'Next step'}
        </Button>
      </div>

      <DiscardConfirmModal
        open={showDiscardConfirm}
        navigateToPath={discardNavigateTo}
        onContinueEditing={handleDiscardConfirmCancel}
        onLeaveSetup={handleDiscardConfirmClose}
      />
    </div>
  );
};

export default InsightDefaultFooter;
